import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  User,
  Calendar,
  TrendingUp,
  LogOut,
  FileBarChart,
  Package,
  Truck,
  ArrowDownToLine,
  ArrowUpFromLine,
  ClipboardCheck,
  MapPin,
  Phone,
  FileText,
  ShoppingCart,
  Settings2,
} from 'lucide-react';
import StatCard from '@/components/ui/StatCard';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '@/api/AuthContext';
import TopKunden from '@/components/contacts/TopKunden';

const ROLE_LABELS = {
  admin: { label: 'Administrator', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  agent: { label: 'Agent', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  warehouse: { label: 'Lager', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  shipping: { label: 'Versand', color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

export default function AgentProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const role = user?.role || 'agent';
  const roleConfig = ROLE_LABELS[role] || ROLE_LABELS.agent;
  const displayName = user?.name || [user?.vorname, user?.nachname].filter(Boolean).join(' ') || 'Benutzer';

  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header — Marka rengi (Deep Royal Blue) */}
      <div className="bg-brand-950 text-white px-4 pt-8 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold tracking-tight">Profil</h1>
            {role === 'agent' && (
              <Link to={createPageUrl('AgentReport')}>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 min-h-[44px] min-w-[44px]">
                  <FileBarChart className="w-5 h-5" />
                </Button>
              </Link>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 border-2 border-accent/40">
              <AvatarFallback className="bg-accent text-brand-950 text-xl font-black">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold truncate">{displayName}</h2>
              <p className="text-slate-300 truncate">{user?.email}</p>
              <Badge className={`mt-1.5 ${roleConfig.color} border`} variant="outline">
                {roleConfig.label}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Role-specific content */}
      <div className="max-w-7xl mx-auto px-4 -mt-8">
        {role === 'agent' && <AgentStats user={user} />}
        {role === 'warehouse' && <WarehouseStats />}
        {role === 'shipping' && <ShippingStats />}
        {role === 'admin' && <AdminProfileNote />}

        {/* Logout */}
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50 min-h-[44px]"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Abmelden
        </Button>
      </div>
    </div>
  );
}

// ─── Agent stats (orijinal içerik) ─────────────────────────────────────────
function AgentStats({ user }) {
  const currentMonth = {
    start: startOfMonth(new Date()).toISOString().split('T')[0],
    end: endOfMonth(new Date()).toISOString().split('T')[0],
  };

  const { data: activities = [] } = useQuery({
    queryKey: ['agent-activities', user?.email, currentMonth],
    queryFn: async () => {
      const all = await base44.entities.ActivityReport.filter({ agent_email: user?.email });
      return all.filter((a) => a.report_date >= currentMonth.start && a.report_date <= currentMonth.end);
    },
    enabled: !!user?.email,
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ['agent-all-tasks', user?.email],
    queryFn: () => base44.entities.Task.filter({ assigned_agent: user?.email }),
    enabled: !!user?.email,
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['agent-all-orders', user?.email],
    queryFn: () => base44.entities.Order.filter({ agent_email: user?.email }),
    enabled: !!user?.email,
  });

  const stats = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter((t) => t.status === 'Abgeschlossen').length,
    visits: activities.filter((a) => a.activity_type === 'Besuch').length,
    calls: activities.filter((a) => a.activity_type === 'Anruf').length,
    followups: activities.filter((a) => a.activity_type === 'Nachfassen').length,
    proposals: orders.filter((o) => o.order_type === 'Angebot').length,
    orders: orders.filter((o) => o.order_type === 'Bestellung').length,
  };

  const completionRate = stats.totalTasks > 0
    ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
    : 0;

  return (
    <>
      <Card className="shadow-lg mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {format(new Date(), 'MMMM yyyy', { locale: de })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Besuche" value={stats.visits} tone="brand" icon={<MapPin className="w-4 h-4" />} />
            <StatCard label="Anrufe" value={stats.calls} tone="brand" icon={<Phone className="w-4 h-4" />} />
            <StatCard label="Angebote" value={stats.proposals} tone="accent" icon={<FileText className="w-4 h-4" />} />
            <StatCard label="Bestellungen" value={stats.orders} tone="emerald" icon={<ShoppingCart className="w-4 h-4" />} />
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold">Aufgaben-Fortschritt</span>
            </div>
            <span className="text-2xl font-bold text-emerald-600">{completionRate}%</span>
          </div>
          <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${completionRate}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm text-slate-500">
            <span>{stats.completedTasks} abgeschlossen</span>
            <span>{stats.totalTasks} gesamt</span>
          </div>
        </CardContent>
      </Card>

      {/* Top 10 Kunden des Agenten — 5 sichtbar, "Alle anzeigen" → 10 */}
      <TopKunden
        orders={orders}
        initialCount={5}
        expandCount={10}
        title="Meine Top Kunden"
      />

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-slate-600">Nachfassen</span>
              <span className="font-semibold">{stats.followups}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-slate-600">Offene Aufgaben</span>
              <span className="font-semibold">{stats.totalTasks - stats.completedTasks}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-slate-600">Erledigungsrate</span>
              <span className="font-semibold text-emerald-600">{completionRate}%</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// ─── Warehouse stats ───────────────────────────────────────────────────────
function WarehouseStats() {
  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['warehouse-purchase-orders'],
    queryFn: () => base44.entities.PurchaseOrder.list(),
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['warehouse-orders-all'],
    queryFn: () => base44.entities.Order.list(),
  });

  const stats = {
    bestellt: purchaseOrders.filter((p) => p.status === 'bestellt').length,
    abgeschlossen: purchaseOrders.filter((p) => p.status === 'abgeschlossen').length,
    pendingAusgang: orders.filter((o) => o.status !== 'Abgeschlossen' && o.status !== 'Storniert').length,
    completedAusgang: orders.filter((o) => o.status === 'Abgeschlossen').length,
  };

  return (
    <>
      <Card className="shadow-lg mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
            <Package className="w-4 h-4" />
            Lager-Übersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-emerald-50 rounded-xl">
              <ArrowDownToLine className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-3xl font-bold text-emerald-600">{stats.bestellt}</p>
              <p className="text-xs text-emerald-600/70">Erwartete Eingänge</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-xl">
              <ArrowUpFromLine className="w-5 h-5 text-blue-600 mx-auto mb-1" />
              <p className="text-3xl font-bold text-blue-600">{stats.pendingAusgang}</p>
              <p className="text-xs text-blue-600/70">Offene Ausgänge</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <ClipboardCheck className="w-5 h-5 text-slate-600 mx-auto mb-1" />
              <p className="text-3xl font-bold text-slate-700">{stats.abgeschlossen}</p>
              <p className="text-xs text-slate-500">Abgeschlossene Eingänge</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <ClipboardCheck className="w-5 h-5 text-amber-600 mx-auto mb-1" />
              <p className="text-3xl font-bold text-amber-600">{stats.completedAusgang}</p>
              <p className="text-xs text-amber-600/70">Abgeschlossene Ausgänge</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

// ─── Shipping stats ────────────────────────────────────────────────────────
function ShippingStats() {
  const { data: orders = [] } = useQuery({
    queryKey: ['shipping-orders-all'],
    queryFn: () => base44.entities.Order.list(),
  });

  const today = new Date().toISOString().split('T')[0];
  const stats = {
    pending: orders.filter((o) => o.status === 'Pending' || o.status === 'Confirmed').length,
    shippedToday: orders.filter((o) => {
      const upd = o.updated_at || o.updated_date;
      return o.status === 'Shipped' && upd && upd.startsWith(today);
    }).length,
    shippedTotal: orders.filter((o) => o.status === 'Shipped').length,
  };

  return (
    <Card className="shadow-lg mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
          <Truck className="w-4 h-4" />
          Versand-Übersicht
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-4 bg-amber-50 rounded-xl">
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-xs text-amber-600/70">Offen</p>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl">
            <p className="text-2xl font-bold text-emerald-600">{stats.shippedToday}</p>
            <p className="text-xs text-emerald-600/70">Heute</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-xl">
            <p className="text-2xl font-bold text-blue-600">{stats.shippedTotal}</p>
            <p className="text-xs text-blue-600/70">Gesamt</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Admin profile note ────────────────────────────────────────────────────
function AdminProfileNote() {
  return (
    <Card className="shadow-lg mb-6">
      <CardContent className="p-6 text-center">
        <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-sm text-slate-600 mb-1">Sie sind als Administrator angemeldet</p>
        <p className="text-xs text-slate-400">
          Verwalten Sie das System über den Admin-Bereich
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <Link to={createPageUrl('AdminDashboard')} className="block">
            <Button className="w-full bg-brand-950 hover:bg-brand-900 text-white min-h-[44px]">
              Zum Admin-Dashboard
            </Button>
          </Link>
          <Link to={createPageUrl('AdminEinstellungen')} className="block">
            <Button variant="outline" className="w-full min-h-[44px]">
              <Settings2 className="w-4 h-4 mr-2" />
              Einstellungen
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
